import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as Fi from 'react-icons/fi';

/* ── Full Court SVG (放大版 Modal — 優化版) ── */
const FullCourt = ({ serverIsOnTop, serverBoxScreenRight, receiverBoxScreenRight, serverColor, receiverColor, topColor, bottomColor, topLabel, bottomLabel }) => {
  const VW = 280, VH = 420, pad = 15, netY = VH / 2;
  const cL = pad, cR = VW - pad, cT = pad, cB = VH - pad, cMX = VW / 2;
  const aFY = netY - (netY - cT) * 0.42;
  const bFY = netY + (cB - netY) * 0.42;

  const getPos = (isTop, boxRight) => ({
    x: boxRight ? (cMX + cR) / 2 : (cL + cMX) / 2,
    y: isTop ? (cT + aFY) / 2 : (bFY + cB) / 2,
  });

  const serverPos = getPos(serverIsOnTop, serverBoxScreenRight);
  const receiverPos = getPos(!serverIsOnTop, receiverBoxScreenRight);

  const sBoxX = serverBoxScreenRight ? cMX : cL;
  const sBoxY = serverIsOnTop ? cT : bFY;
  const sBoxW = cMX - cL;
  const sBoxH = serverIsOnTop ? aFY - cT : cB - bFY;

  const rBoxX = receiverBoxScreenRight ? cMX : cL;
  const rBoxY = !serverIsOnTop ? cT : bFY;
  const rBoxH = !serverIsOnTop ? aFY - cT : cB - bFY;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {/* 背景 */}
      <rect width={VW} height={VH} fill="rgba(10,18,32,0.95)" rx={20} />
      
      {/* 場地背景色 */}
      <rect x={cL} y={cT} width={cR - cL} height={netY - cT} fill={`${topColor}10`} />
      <rect x={cL} y={netY} width={cR - cL} height={cB - netY} fill={`${bottomColor}10`} />
      
      {/* 場地外框 */}
      <rect x={cL} y={cT} width={cR - cL} height={cB - cT} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" rx="8" />
      
      {/* 發球線 */}
      <line x1={cL} y1={aFY} x2={cR} y2={aFY} stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
      <line x1={cL} y1={bFY} x2={cR} y2={bFY} stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
      
      {/* 中線 */}
      <line x1={cMX} y1={cT} x2={cMX} y2={cB} stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeDasharray="8,6" />
      
      {/* 發球區高亮 */}
      <rect x={sBoxX} y={sBoxY} width={sBoxW} height={sBoxH} fill={`${serverColor}25`} stroke={`${serverColor}`} strokeWidth="3" rx="6" />
      
      {/* 接球區 */}
      <rect x={rBoxX} y={rBoxY} width={sBoxW} height={rBoxH} fill={`${receiverColor}12`} stroke={`${receiverColor}60`} strokeWidth="2" rx="6" />
      
      {/* 球網 */}
      <line x1={cL} y1={netY} x2={cR} y2={netY} stroke="rgba(255,255,255,0.95)" strokeWidth="6" strokeLinecap="round" />
      <text x={cMX} y={netY - 10} textAnchor="middle" fontSize="14" fill="rgba(255,255,255,0.9)" fontWeight="bold">球網</text>
      
      {/* 發球員 — 超大圖標 */}
      <circle cx={serverPos.x} cy={serverPos.y} r={28} fill={serverColor} opacity="0.95" />
      <text x={serverPos.x} y={serverPos.y + 3} textAnchor="middle" dominantBaseline="middle" fontSize="24">🏸</text>
      
      {/* 接球員 */}
      <circle cx={receiverPos.x} cy={receiverPos.y} r={20} fill={receiverColor} opacity="0.75" />
      <circle cx={receiverPos.x} cy={receiverPos.y} r={8} fill="rgba(255,255,255,0.95)" />
      
      {/* 隊伍標籤 — 更大更清楚 */}
      <rect x={cL + 8} y={cT + 8} width={cR - cL - 16} height={28} fill={`${topColor}30`} rx="8" />
      <text x={cMX} y={cT + 24} textAnchor="middle" fontSize="16" fill={topColor} fontWeight="bold" opacity="0.95">{topLabel}</text>
      
      <rect x={cL + 8} y={cB - 36} width={cR - cL - 16} height={28} fill={`${bottomColor}30`} rx="8" />
      <text x={cMX} y={cB - 18} textAnchor="middle" fontSize="16" fill={bottomColor} fontWeight="bold" opacity="0.95">{bottomLabel}</text>
    </svg>
  );
};

/* ── Mini Court SVG (超級放大手機版 — 再加大 20%) ── */
const MiniCourt = ({ serverIsOnTop, serverBoxScreenRight, serverColor, receiverColor, topColor, bottomColor }) => {
  const VW = 72, VH = 100, pad = 3.5, netY = VH / 2;
  const cL = pad, cR = VW - pad, cT = pad, cB = VH - pad, cMX = VW / 2;
  const aFY = netY - (netY - cT) * 0.38;
  const bFY = netY + (cB - netY) * 0.38;

  const sBoxX = serverBoxScreenRight ? cMX : cL;
  const sBoxY = serverIsOnTop ? cT : bFY;
  const sBoxW = cMX - cL;
  const sBoxH = serverIsOnTop ? aFY - cT : cB - bFY;

  const sx = serverBoxScreenRight ? (cMX + cR) / 2 : (cL + cMX) / 2;
  const sy = serverIsOnTop ? (cT + aFY) / 2 : (bFY + cB) / 2;

  const receiverBoxScreenRight = !serverBoxScreenRight;
  const rx = receiverBoxScreenRight ? (cMX + cR) / 2 : (cL + cMX) / 2;
  const ry = !serverIsOnTop ? (cT + aFY) / 2 : (bFY + cB) / 2;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full h-full block drop-shadow-lg" preserveAspectRatio="xMidYMid meet">
      <rect x={cL} y={cT} width={cR - cL} height={netY - cT} fill={`${topColor}20`} />
      <rect x={cL} y={netY} width={cR - cL} height={cB - netY} fill={`${bottomColor}20`} />
      <rect x={cL} y={cT} width={cR - cL} height={cB - cT} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.4" rx="3" />
      <line x1={cL} y1={aFY} x2={cR} y2={aFY} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
      <line x1={cL} y1={bFY} x2={cR} y2={bFY} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
      <line x1={cMX} y1={cT} x2={cMX} y2={cB} stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="3,3" />
      <rect x={sBoxX} y={sBoxY} width={sBoxW} height={sBoxH} fill={`${serverColor}50`} stroke={`${serverColor}95`} strokeWidth="2" rx="2.5" />
      <line x1={cL} y1={netY} x2={cR} y2={netY} stroke="rgba(255,255,255,0.95)" strokeWidth="3.5" strokeLinecap="round" />
      <circle cx={sx} cy={sy} r={7.5} fill={serverColor} opacity="0.95" />
      <text x={sx} y={sy + 0.6} textAnchor="middle" dominantBaseline="middle" fontSize="8.5" opacity="0.95">🏸</text>
      <circle cx={rx} cy={ry} r={6} fill={receiverColor} opacity="0.85" />
      <circle cx={rx} cy={ry} r={2.5} fill="rgba(255,255,255,0.9)" />
    </svg>
  );
};

/* ── Main ── */
const CourtDiagram = ({ serving, serveSide, swapped, teamNameA = '隊伍 A', teamNameB = '隊伍 B' }) => {
  const [expanded, setExpanded] = useState(false);

  // 鎖定背景滾動 (當放大 Modal 開啟時)
  useEffect(() => {
    if (expanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expanded]);

  const topTeam = swapped ? 'B' : 'A';
  const serverIsOnTop = serving === topTeam;
  const serverBoxScreenRight = serverIsOnTop ? serveSide === 'right' : serveSide === 'left';
  const receiverBoxScreenRight = !serverBoxScreenRight;

  const aColor = '#818cf8', bColor = '#fb7185';
  const topColor = swapped ? bColor : aColor;
  const bottomColor = swapped ? aColor : bColor;
  const serverColor = serving === 'A' ? aColor : bColor;
  const receiverColor = serving === 'A' ? bColor : aColor;

  const courtProps = {
    serverIsOnTop,
    serverBoxScreenRight,
    receiverBoxScreenRight,
    serverColor,
    receiverColor,
    topColor,
    bottomColor,
    topLabel: swapped ? teamNameB : teamNameA,
    bottomLabel: swapped ? teamNameA : teamNameB,
  };

  // 使用 React Portal 將 Modal 渲染到 document.body 之下，避免受到父層 translate 和寬度限制的影響
  const modalContent = (
    <AnimatePresence>
      {expanded && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/92 backdrop-blur-md"
          />
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 10 }}
            transition={{ type: 'spring', damping: 24, stiffness: 300 }}
            className="relative z-10 flex flex-col items-center gap-5 p-6 rounded-3xl bg-[#0a1220] border border-white/10 shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-lg font-black text-white">發球位置</p>
                <p className="text-xs text-white/40 font-bold mt-0.5">點擊場外關閉</p>
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="text-white/40 hover:text-white bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition-colors text-lg"
              >
                <Fi.FiX />
              </button>
            </div>

            {/* 場地圖 — 優化尺寸和比例 */}
            <div className="w-full rounded-2xl overflow-hidden border border-white/15 shadow-xl" style={{ aspectRatio: '280 / 420' }}>
              <FullCourt {...courtProps} />
            </div>

            {/* 圖例 — 更清楚的設計 */}
            <div className="w-full grid grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0" style={{ backgroundColor: serverColor }}>
                  🏸
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-white">發球員</p>
                  <p className="text-[10px] text-white/40 font-bold truncate">
                    {serving === 'A' ? teamNameA : teamNameB}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: receiverColor, opacity: 0.78 }}>
                  <div className="w-4 h-4 rounded-full bg-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-white">接球員</p>
                  <p className="text-[10px] text-white/40 font-bold truncate">
                    {serving === 'A' ? teamNameB : teamNameA}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* ── Inline mini icon (超級放大手機優化 — 72×100) ── */}
      <button
        onClick={() => setExpanded(true)}
        className="flex flex-col items-center w-full gap-2 active:scale-95 transition-transform mx-auto"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <div
          className="w-full rounded-xl overflow-hidden border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          style={{
            aspectRatio: '72 / 100',
            background: 'rgba(15,23,42,0.75)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
        >
          <MiniCourt
            serverIsOnTop={serverIsOnTop}
            serverBoxScreenRight={serverBoxScreenRight}
            serverColor={serverColor}
            receiverColor={receiverColor}
            topColor={topColor}
            bottomColor={bottomColor}
          />
        </div>
        <div className="flex items-center gap-1.5 opacity-60 hover:opacity-100 transition-opacity bg-black/40 px-2 py-0.5 rounded-full backdrop-blur-sm">
          <span className="font-black tracking-widest uppercase whitespace-nowrap text-[9px] md:text-[11px]">
            場地
          </span>
          <Fi.FiMaximize2 size={10} />
        </div>
      </button>

      {/* ── Modal (使用 Portal 防止變形) ── */}
      {typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent}
    </>
  );
};

export default CourtDiagram;